import Grid from '@mui/material/Grid';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import routes from '../../config/routes';

import {
  reportMastersImportLoadingSelector,
  findMastersImportRequested,
  reportMastersImportFilterEndImportDateSelector,
  reportMastersImportFilterStartImportDateSelector,
  activeCountriesSelector,
} from '../../state';

import { ReportActionsGridItem, ReportFilterActions, ReportFilters } from '../ReportFilters';

import { AppPermissions } from '../../types';
import { PermissionsProvider } from '../Permissions';
import { CountryFilter, ImportDateRangeFilter, StateFilter } from './filters';
import { FilterPanel, StatusChip } from '../Visual';
import { BaseButton } from '../base';

interface ButtonCreateProps {
  onCreate?: () => void;
}

const ButtonCreate = ({ onCreate }: ButtonCreateProps) => {
  const navigate = useNavigate();
  const handleClickApply = useCallback(
    () =>
      onCreate
        ? onCreate()
        : navigate(routes.protectedNestedRoutes.mastersImport.children.create.path),
    [navigate, onCreate]
  );
  const { t } = useTranslation();
  const { loaded } = useSelector(activeCountriesSelector);
  const disabled = !loaded;

  return (
    <BaseButton variant="contained" onClick={handleClickApply} disabled={disabled}>
      {t('masters-import.execution.label')}
    </BaseButton>
  );
};

interface MastersDetailSearchProps {
  onCreate?: () => void;
}

const MastersDetailSearch = ({ onCreate }: MastersDetailSearchProps) => {
  const { t } = useTranslation();
  const startImportDate = !useSelector(reportMastersImportFilterStartImportDateSelector).accept;
  const endImportDate = !useSelector(reportMastersImportFilterEndImportDateSelector).accept;
  const applyDisabled = startImportDate && endImportDate ? false : startImportDate || endImportDate;
  const importDateRequiredIncomplete = applyDisabled;
  return (
    <ReportFilters
      loadingSelector={reportMastersImportLoadingSelector}
      applyAction={findMastersImportRequested}
    >
      <PermissionsProvider resourcePermissions={[AppPermissions.DASHBOARD_MASTER_IMPORT]}>
        <FilterPanel
          subtitleTranslationKey="masters-detail.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth />
            </Grid>
            <Grid item xs={12} md={6} lg={4} xl={3}>
              <ImportDateRangeFilter
                spacing={1}
                requiredIncomplete={importDateRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <StateFilter />
            </Grid>
            <ReportActionsGridItem>
              <ReportFilterActions applyDisabled={applyDisabled}>
                <ButtonCreate onCreate={onCreate} />
              </ReportFilterActions>
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default MastersDetailSearch;
